'use client';

import { type ElementType } from 'react';

import type { ResponsiveOption } from '../../types/responsiveOption';

import {
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../types/component';
import { resolveResponsiveProps } from '../../utils/resolveResponsiveProps';
import { tv } from '../../utils/tv';

type Column = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type Gap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

interface GridBaseProps {
  /**
   * Sets the number of columns in the grid.
   * @default 12
   */
  columns?: ResponsiveOption<Column>;
  /**
   * Sets the gap between columns.
   * @default 2
   */
  columnGap?: ResponsiveOption<Gap>;
  /**
   * Sets the gap between rows.
   * @default 2
   */
  rowGap?: ResponsiveOption<Gap>;
}

const gridClasses = {
  root: 'zds-grid-root',
} as const;

const gridStyles = tv({
  base: [gridClasses.root, 'grid'],
  variants: {
    columns: {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
      4: 'grid-cols-4',
      5: 'grid-cols-5',
      6: 'grid-cols-6',
      7: 'grid-cols-7',
      8: 'grid-cols-8',
      9: 'grid-cols-9',
      10: 'grid-cols-10',
      11: 'grid-cols-11',
      12: 'grid-cols-12',
    },
    columnsXs: {
      1: 'xs:grid-cols-1',
      2: 'xs:grid-cols-2',
      3: 'xs:grid-cols-3',
      4: 'xs:grid-cols-4',
      5: 'xs:grid-cols-5',
      6: 'xs:grid-cols-6',
      7: 'xs:grid-cols-7',
      8: 'xs:grid-cols-8',
      9: 'xs:grid-cols-9',
      10: 'xs:grid-cols-10',
      11: 'xs:grid-cols-11',
      12: 'xs:grid-cols-12',
    },
    columnsSm: {
      1: 'sm:grid-cols-1',
      2: 'sm:grid-cols-2',
      3: 'sm:grid-cols-3',
      4: 'sm:grid-cols-4',
      5: 'sm:grid-cols-5',
      6: 'sm:grid-cols-6',
      7: 'sm:grid-cols-7',
      8: 'sm:grid-cols-8',
      9: 'sm:grid-cols-9',
      10: 'sm:grid-cols-10',
      11: 'sm:grid-cols-11',
      12: 'sm:grid-cols-12',
    },
    columnsMd: {
      1: 'md:grid-cols-1',
      2: 'md:grid-cols-2',
      3: 'md:grid-cols-3',
      4: 'md:grid-cols-4',
      5: 'md:grid-cols-5',
      6: 'md:grid-cols-6',
      7: 'md:grid-cols-7',
      8: 'md:grid-cols-8',
      9: 'md:grid-cols-9',
      10: 'md:grid-cols-10',
      11: 'md:grid-cols-11',
      12: 'md:grid-cols-12',
    },
    columnsLg: {
      1: 'lg:grid-cols-1',
      2: 'lg:grid-cols-2',
      3: 'lg:grid-cols-3',
      4: 'lg:grid-cols-4',
      5: 'lg:grid-cols-5',
      6: 'lg:grid-cols-6',
      7: 'lg:grid-cols-7',
      8: 'lg:grid-cols-8',
      9: 'lg:grid-cols-9',
      10: 'lg:grid-cols-10',
      11: 'lg:grid-cols-11',
      12: 'lg:grid-cols-12',
    },
    columnsXl: {
      1: 'xl:grid-cols-1',
      2: 'xl:grid-cols-2',
      3: 'xl:grid-cols-3',
      4: 'xl:grid-cols-4',
      5: 'xl:grid-cols-5',
      6: 'xl:grid-cols-6',
      7: 'xl:grid-cols-7',
      8: 'xl:grid-cols-8',
      9: 'xl:grid-cols-9',
      10: 'xl:grid-cols-10',
      11: 'xl:grid-cols-11',
      12: 'xl:grid-cols-12',
    },
    columnGap: {
      0: 'gap-x-0',
      1: 'gap-x-1',
      2: 'gap-x-2',
      3: 'gap-x-3',
      4: 'gap-x-4',
      5: 'gap-x-5',
      6: 'gap-x-6',
      7: 'gap-x-7',
      8: 'gap-x-8',
      9: 'gap-x-9',
      10: 'gap-x-10',
    },
    columnGapXs: {
      0: 'xs:gap-x-0',
      1: 'xs:gap-x-1',
      2: 'xs:gap-x-2',
      3: 'xs:gap-x-3',
      4: 'xs:gap-x-4',
      5: 'xs:gap-x-5',
      6: 'xs:gap-x-6',
      7: 'xs:gap-x-7',
      8: 'xs:gap-x-8',
      9: 'xs:gap-x-9',
      10: 'xs:gap-x-10',
    },
    columnGapSm: {
      0: 'sm:gap-x-0',
      1: 'sm:gap-x-1',
      2: 'sm:gap-x-2',
      3: 'sm:gap-x-3',
      4: 'sm:gap-x-4',
      5: 'sm:gap-x-5',
      6: 'sm:gap-x-6',
      7: 'sm:gap-x-7',
      8: 'sm:gap-x-8',
      9: 'sm:gap-x-9',
      10: 'sm:gap-x-10',
    },
    columnGapMd: {
      0: 'md:gap-x-0',
      1: 'md:gap-x-1',
      2: 'md:gap-x-2',
      3: 'md:gap-x-3',
      4: 'md:gap-x-4',
      5: 'md:gap-x-5',
      6: 'md:gap-x-6',
      7: 'md:gap-x-7',
      8: 'md:gap-x-8',
      9: 'md:gap-x-9',
      10: 'md:gap-x-10',
    },
    columnGapLg: {
      0: 'lg:gap-x-0',
      1: 'lg:gap-x-1',
      2: 'lg:gap-x-2',
      3: 'lg:gap-x-3',
      4: 'lg:gap-x-4',
      5: 'lg:gap-x-5',
      6: 'lg:gap-x-6',
      7: 'lg:gap-x-7',
      8: 'lg:gap-x-8',
      9: 'lg:gap-x-9',
      10: 'lg:gap-x-10',
    },
    columnGapXl: {
      0: 'xl:gap-x-0',
      1: 'xl:gap-x-1',
      2: 'xl:gap-x-2',
      3: 'xl:gap-x-3',
      4: 'xl:gap-x-4',
      5: 'xl:gap-x-5',
      6: 'xl:gap-x-6',
      7: 'xl:gap-x-7',
      8: 'xl:gap-x-8',
      9: 'xl:gap-x-9',
      10: 'xl:gap-x-10',
    },
    rowGap: {
      0: 'gap-y-0',
      1: 'gap-y-1',
      2: 'gap-y-2',
      3: 'gap-y-3',
      4: 'gap-y-4',
      5: 'gap-y-5',
      6: 'gap-y-6',
      7: 'gap-y-7',
      8: 'gap-y-8',
      9: 'gap-y-9',
      10: 'gap-y-10',
    },
    rowGapXs: {
      0: 'xs:gap-y-0',
      1: 'xs:gap-y-1',
      2: 'xs:gap-y-2',
      3: 'xs:gap-y-3',
      4: 'xs:gap-y-4',
      5: 'xs:gap-y-5',
      6: 'xs:gap-y-6',
      7: 'xs:gap-y-7',
      8: 'xs:gap-y-8',
      9: 'xs:gap-y-9',
      10: 'xs:gap-y-10',
    },
    rowGapSm: {
      0: 'sm:gap-y-0',
      1: 'sm:gap-y-1',
      2: 'sm:gap-y-2',
      3: 'sm:gap-y-3',
      4: 'sm:gap-y-4',
      5: 'sm:gap-y-5',
      6: 'sm:gap-y-6',
      7: 'sm:gap-y-7',
      8: 'sm:gap-y-8',
      9: 'sm:gap-y-9',
      10: 'sm:gap-y-10',
    },
    rowGapMd: {
      0: 'md:gap-y-0',
      1: 'md:gap-y-1',
      2: 'md:gap-y-2',
      3: 'md:gap-y-3',
      4: 'md:gap-y-4',
      5: 'md:gap-y-5',
      6: 'md:gap-y-6',
      7: 'md:gap-y-7',
      8: 'md:gap-y-8',
      9: 'md:gap-y-9',
      10: 'md:gap-y-10',
    },
    rowGapLg: {
      0: 'lg:gap-y-0',
      1: 'lg:gap-y-1',
      2: 'lg:gap-y-2',
      3: 'lg:gap-y-3',
      4: 'lg:gap-y-4',
      5: 'lg:gap-y-5',
      6: 'lg:gap-y-6',
      7: 'lg:gap-y-7',
      8: 'lg:gap-y-8',
      9: 'lg:gap-y-9',
      10: 'lg:gap-y-10',
    },
    rowGapXl: {
      0: 'xl:gap-y-0',
      1: 'xl:gap-y-1',
      2: 'xl:gap-y-2',
      3: 'xl:gap-y-3',
      4: 'xl:gap-y-4',
      5: 'xl:gap-y-5',
      6: 'xl:gap-y-6',
      7: 'xl:gap-y-7',
      8: 'xl:gap-y-8',
      9: 'xl:gap-y-9',
      10: 'xl:gap-y-10',
    },
  },
});

type GridProps<RootComponent extends ElementType = 'div'> =
  PolymorphicComponentProps<GridBaseProps, RootComponent>;

type GridComponent = PolymorphicComponent<GridBaseProps, 'div'>;

const Grid: GridComponent = (props) => {
  const {
    component: Root = 'div',
    columns = 12,
    columnGap = 2,
    rowGap = 2,
    className,
    children,
    ref,
    ...rest
  } = props;

  const {
    defaultOption: columnDefaultOption,
    responsiveOption: columnResponsiveOption,
  } = resolveResponsiveProps(columns);
  const {
    defaultOption: columnGapDefaultOption,
    responsiveOption: columnGapResponsiveOption,
  } = resolveResponsiveProps(columnGap);
  const {
    defaultOption: rowGapDefaultOption,
    responsiveOption: rowGapResponsiveOption,
  } = resolveResponsiveProps(rowGap);

  const styles = gridStyles({
    columns: columnDefaultOption,
    columnsXs: columnResponsiveOption?.xs,
    columnsSm: columnResponsiveOption?.sm,
    columnsMd: columnResponsiveOption?.md,
    columnsLg: columnResponsiveOption?.lg,
    columnsXl: columnResponsiveOption?.xl,

    columnGap: columnGapDefaultOption,
    columnGapXs: columnGapResponsiveOption?.xs,
    columnGapSm: columnGapResponsiveOption?.sm,
    columnGapMd: columnGapResponsiveOption?.md,
    columnGapLg: columnGapResponsiveOption?.lg,
    columnGapXl: columnGapResponsiveOption?.xl,

    rowGap: rowGapDefaultOption,
    rowGapXs: rowGapResponsiveOption?.xs,
    rowGapSm: rowGapResponsiveOption?.sm,
    rowGapMd: rowGapResponsiveOption?.md,
    rowGapLg: rowGapResponsiveOption?.lg,
    rowGapXl: rowGapResponsiveOption?.xl,
    className,
  });

  return (
    <Root ref={ref} className={styles} {...rest}>
      {children}
    </Root>
  );
};

Grid.displayName = 'Grid';

export { Grid, type GridProps, type GridBaseProps, type Column, gridClasses };
